import React, { useEffect, useState } from 'react';
import Modal from '../components/Modal';
import { isPractitionerSupportsMedicare, isMedicareOrTricareSubscriberId } from '../util/MedicareUtil';
import MedicareAdvantagePopover from '../components/MedicareAdvantagePopover';

/**
 * Displays a modal, asking the user to confirm whether the entered insurance details are Medicare.
 *
 * @param subscriberId the insurance subsriber ID used to determine if this might be Medicare insurance
 * @param onConfirm the function to call when the insurance is confirmed to be Medicare
 * @param gearOrder the order details
 * @param userInfo the user details
 * @param updateStep a function that will navigate to a different step in the application (Used to change billing methods)
 */
export default function MedicareConfirmation({ subsriberId, onConfirm, gearOrder, userInfo, updateStep }) {
	const [showModal, setShowModal] = useState(false);
	const [practSupportsMedicare] = useState(isPractitionerSupportsMedicare(gearOrder?.practitioner));

	// Show the confirmation modal any time the subscriber ID changes to a possible Medicare value
	useEffect(() => {
		let isMBI = isMedicareOrTricareSubscriberId(subsriberId);
		setShowModal(isMBI);
	}, [subsriberId]);

	/**
	 * Hide the modal when "No" is the answer.
	 * We don't need to pass anything back to Insurance. Closing the modal will allow the user to
	 * continue entering information.
	 */
	const onAnswerNo = () => {
		setShowModal(false);
	};

	/**
	 * Hide the modal and call props.onConfirm() when "Yes" is the answer.
	 */
	const onAnswerYes = () => {
		setShowModal(false);

		onConfirm && onConfirm();
	};

	/**
	 * The Modal will call this method when an attempt is made to close the Modal by clicking
	 *  outside or pressing 'Escape'.
	 * Prevent the event propagation and return false to disable auto-close behavior.
	 */
	const preventClose = (evt) => {
		evt.preventDefault();
		return false;
	};

	if (!showModal || !gearOrder?.requisitions?.length) {
		return null;
	}

	let message, cancelAction, confirmAction;

	if (!practSupportsMedicare) {
		cancelAction = 'No';
		confirmAction = 'Yes';
		message = (
			<div>
				<h4>If this order is for a Medicare or Tricare patient:</h4>
				<p>
					The ordering physician is not authorized or is not setup in our system to order testing on Medicare or Tricare beneficiaries.
					<b>You may be billed directly if you supply Medicare information.</b> Please contact your physician with any questions.
				</p>
				<h4>
					If this order is for a patient with a <MedicareAdvantagePopover />, or for a patient who doesn't have
					Medicare:
				</h4>
				<p>
					Select <b>No</b>, and continue filling out insurance information.
					<br />
					If the patient has a <MedicareAdvantagePopover />, please enter the Medicare Advantage ID and
					insurance information.
				</p>
			</div>
		);
	} else if (userInfo.type === 'prc') {
		cancelAction = 'No';
		confirmAction = 'Yes';
		message = (
			<div>
				<h4>If this order is for a Medicare or Tricare patient:</h4>
				<ol>
					<li>
						Please contact the physician to ask that they register this test online with ID number{' '}
						{gearOrder.requisitions[0].reqNumber}.
					</li>
					<li>
						Select <b>Yes</b> to save your Medicare/Tricare insurance information.
						<br />
						<br />
						<b>Important:</b> If the physician does not complete their portion of the online order for a
						Medicare/Tricare patient, it will result in the test being discarded when it arrives at the
						laboratory.
					</li>
				</ol>
				<h4>
					If this order is for a patient with a <MedicareAdvantagePopover />, or for a patient who doesn't have
					Medicare or Tricare:
				</h4>
				<ul style={{ listStyle: 'none' }}>
					<li>Select <b>No</b>, and continue filling out insurance information.</li>
					<li>
						If the patient has a <MedicareAdvantagePopover />, please enter the Medicare Advantage ID and
						insurance information.
					</li>
				</ul>
			</div>
		);
	} else {
		cancelAction = 'No';
		confirmAction = 'Yes';
		message = (
			<p>
				The insurance subscriber ID you entered appears to be a Medicare or Tricare ID. If this order is for a
				Medicare patient, please click <b>Yes</b>.
				<br />
				<br />
				If the patient has a <MedicareAdvantagePopover />, please click <b>No</b> and enter the Medicare Advantage ID
				and insurance information.
			</p>
		);
	}

	return (
		<Modal
			title="Is this order for a Medicare or Tricare patient?"
			id="mbiConfirmation-modal"
			doConfirm={preventClose}
			hideCloseX={true}
			cancelAction={cancelAction}
			onCancel={onAnswerNo}
			confirmAction={confirmAction}
			onSave={onAnswerYes}
		>
			<div>{message}</div>
		</Modal>
	);
}
